import { useState, useCallback } from 'react'

import { useAppDispatch } from 'redux/Hook'
import { updatePhrases } from 'redux/thunks/Themes'
import { useAlert } from 'components/Shared/Alert'
import type { Theme } from 'containers/TextAnalysis/AIThemes/ThemeKeyPhrasesModal/Types'
import { truncateText } from 'containers/TextAnalysis/Helpers/ThemesHelper'

interface LoadingState {
  isLoading: boolean
  message: string
}

export const useMoveKeyPhrases = () => {
  const dispatch = useAppDispatch()
  const { showAlert } = useAlert()
  const [loading, setLoading] = useState<LoadingState>({
    isLoading: false,
    message: '',
  })

  const setLoadingState = useCallback((isLoading: boolean, message = '') => {
    setLoading({ isLoading, message })
  }, [])

  const executeMoveKeyPhrase = useCallback(
    async (
      targetThemeId: string,
      keyPhrase: string,
      targetThemeKeyPhrases: string[],
    ) => {
      const updatedTargetKeyPhrases = [...targetThemeKeyPhrases, keyPhrase]

      await dispatch(
        updatePhrases({
          themeId: targetThemeId,
          keyPhrases: updatedTargetKeyPhrases,
        }),
      ).unwrap()
    },
    [dispatch],
  )

  const executeDeleteKeyPhrase = useCallback(
    async (sourceThemeId: string, keyPhrase: string, keyPhrases: string[]) => {
      const updatedSourceKeyPhrases = keyPhrases.filter(
        (phrase) => phrase !== keyPhrase,
      )
      await dispatch(
        updatePhrases({
          themeId: sourceThemeId,
          keyPhrases: updatedSourceKeyPhrases,
        }),
      ).unwrap()
    },
    [dispatch],
  )

  const getKeyPhrases = (theme: Theme): string[] =>
    theme.attributes?.query?.attributes?.key_phrases || []

  // Handle moving a key phrase
  const moveKeyPhrase = useCallback(
    async (keyPhrase: string, sourceTheme: Theme, targetTheme: Theme) => {
      if (!keyPhrase) return

      try {
        setLoadingState(true, 'Moving key phrase')
        const allSourceKeyPhrases = getKeyPhrases(sourceTheme)
        const targetThemeKeyPhrases = getKeyPhrases(targetTheme)

        // Execute API calls:
        await executeMoveKeyPhrase(
          targetTheme.id,
          keyPhrase,
          targetThemeKeyPhrases,
        )
        await executeDeleteKeyPhrase(
          sourceTheme.id,
          keyPhrase,
          allSourceKeyPhrases,
        )

        showAlert({
          type: 'success',
          title: `Moved key phrase "${truncateText(keyPhrase, 30)}" to ${
            targetTheme.attributes.name
          }`,
        })

        return true
      } catch {
        return false
      } finally {
        setLoadingState(false)
      }
    },
    [
      setLoadingState,
      executeMoveKeyPhrase,
      executeDeleteKeyPhrase,
      truncateText,
      showAlert,
    ],
  )

  return { loading, moveKeyPhrase }
}
